import { StoryFragment, TheoryDef } from '../types';
import { STORY_FRAGMENTS, THEORIES } from '../data/stories';

export class StoryManager {
  discoveredFragments: Map<string, StoryFragment> = new Map();
  submittedTheories: Map<string, boolean> = new Map();

  discoverFragment(artifactId: string): StoryFragment | null {
    const fragment = STORY_FRAGMENTS.find(f => f.artifactId === artifactId);
    if (!fragment) return null;
    if (this.discoveredFragments.has(fragment.id)) return null;

    this.discoveredFragments.set(fragment.id, fragment);
    return fragment;
  }

  getSiteFragments(siteId: string): StoryFragment[] {
    return STORY_FRAGMENTS
      .filter(f => f.siteId === siteId)
      .sort((a, b) => a.order - b.order);
  }

  getDiscoveredFragments(siteId: string): StoryFragment[] {
    return Array.from(this.discoveredFragments.values())
      .filter(f => f.siteId === siteId)
      .sort((a, b) => a.order - b.order);
  }

  getCompletionPercent(siteId: string): number {
    const total = STORY_FRAGMENTS.filter(f => f.siteId === siteId).length;
    if (total === 0) return 0;
    const discovered = this.getDiscoveredFragments(siteId).length;
    return (discovered / total) * 100;
  }

  getTheoriesForSite(siteId: string): TheoryDef[] {
    return THEORIES.filter(t => t.siteId === siteId);
  }

  getAvailableTheories(siteId: string): TheoryDef[] {
    const completion = this.getCompletionPercent(siteId);
    return THEORIES.filter(theory => {
      if (theory.siteId !== siteId) return false;
      if (this.submittedTheories.has(theory.id)) return false;
      if (completion < theory.requiredFragments) return false;
      return true;
    });
  }

  submitTheory(theoryId: string, chosenIndex: number): { correct: boolean; repChange: number; loreText: string | null } {
    const theory = THEORIES.find(t => t.id === theoryId);
    if (!theory) {
      return { correct: false, repChange: 0, loreText: null };
    }

    if (this.submittedTheories.has(theoryId)) {
      return { correct: false, repChange: 0, loreText: null };
    }

    const correct = chosenIndex === theory.correctIndex;
    this.submittedTheories.set(theoryId, correct);

    if (correct) {
      return {
        correct: true,
        repChange: theory.repReward,
        loreText: theory.loreText,
      };
    } else {
      return {
        correct: false,
        repChange: -2,
        loreText: null,
      };
    }
  }

  getConnections(siteId: string): { from: StoryFragment; to: StoryFragment }[] {
    const discovered = this.getDiscoveredFragments(siteId);
    const discoveredIds = new Set(discovered.map(f => f.id));
    const connections: { from: StoryFragment; to: StoryFragment }[] = [];

    for (const fragment of discovered) {
      if (fragment.connection && discoveredIds.has(fragment.connection)) {
        const target = this.discoveredFragments.get(fragment.connection);
        if (target) {
          connections.push({ from: fragment, to: target });
        }
      }
    }

    return connections;
  }

  isTheorySubmitted(theoryId: string): boolean {
    return this.submittedTheories.has(theoryId);
  }

  getTimeline(siteId: string): (StoryFragment | null)[] {
    const allSiteFragments = this.getSiteFragments(siteId);
    const discoveredIds = new Set(
      this.getDiscoveredFragments(siteId).map(f => f.id)
    );

    return allSiteFragments.map(fragment =>
      discoveredIds.has(fragment.id) ? fragment : null
    );
  }
}
